import { useEffect, useState } from "react";
import { getProfile, updateProfile, changePassword } from "../api/users";

export default function Account() {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getProfile()
      .then(setProfile)
      .catch((err) => setError(err.message));
  }, []);

  if (error && !profile) return <div className="alert alert-error">{error}</div>;
  if (!profile) return <div className="spinner" />;

  return (
    <div>
      <h2 className="page-title">My Account</h2>
      <p className="page-sub">Your profile and security settings</p>

      <div className="grid-2">
        <ProfileCard profile={profile} onUpdated={setProfile} />
        <PasswordCard />
      </div>
    </div>
  );
}

function ProfileCard({ profile, onUpdated }) {
  const [fullName, setFullName] = useState(profile.full_name || "");
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setMsg(null);
    setSaving(true);
    try {
      const updated = await updateProfile({ full_name: fullName || null });
      onUpdated(updated);
      setMsg({ ok: true, text: "Profile updated." });
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h2>Profile</h2>
      <p className="desc">Basic account information</p>

      {msg && (
        <div className={`alert ${msg.ok ? "alert-success" : "alert-error"}`}>{msg.text}</div>
      )}

      <form onSubmit={save}>
        <div className="field">
          <label>Email</label>
          <input className="input" value={profile.email} disabled />
        </div>
        <div className="field">
          <label>Role</label>
          <input className="input" value={profile.role} disabled />
        </div>
        <div className="field">
          <label>Full name</label>
          <input
            className="input"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Your name"
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Saving…" : "Save profile"}
        </button>
      </form>
    </div>
  );
}

function PasswordCard() {
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setMsg(null);

    if (newPass.length < 8) {
      setMsg({ ok: false, text: "New password must be at least 8 characters." });
      return;
    }
    if (newPass !== confirm) {
      setMsg({ ok: false, text: "New passwords do not match." });
      return;
    }

    setSaving(true);
    try {
      await changePassword(oldPass, newPass);
      setMsg({ ok: true, text: "Password changed." });
      setOldPass("");
      setNewPass("");
      setConfirm("");
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h2>Change password</h2>
      <p className="desc">Use at least 8 characters</p>

      {msg && (
        <div className={`alert ${msg.ok ? "alert-success" : "alert-error"}`}>{msg.text}</div>
      )}

      <form onSubmit={save}>
        <div className="field">
          <label>Current password</label>
          <input
            className="input"
            type="password"
            value={oldPass}
            onChange={(e) => setOldPass(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>
        <div className="field">
          <label>New password</label>
          <input
            className="input"
            type="password"
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>
        <div className="field">
          <label>Confirm new password</label>
          <input
            className="input"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Changing…" : "Change password"}
        </button>
      </form>
    </div>
  );
}
